import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Link } from 'react-router-dom';
import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import { ResultGrid } from '../../../../../grid/ResultGrid';
import { Column } from '../../../../../grid/Column';
import { DeleteIcon } from '../../../../../icon/DeleteIcon';
import { EditIcon } from '../../../../../icon/EditIcon';
import { ExternalLinkIcon } from '../../../../../table/ExternalLinkIcon';
import { DownloadDataIcon } from '../../../../../icon/DownloadDataIcon';
import { visibleColumns as defaultColumns, sort as defaultSort, minColumns } from '../../../../../../redux/tsunamis/marigrams/data/marigramReduxConstants';

const renderEditLink = id => (
  <Link aria-label={`Edit Marigram ${id}`} to={`/view/hazards/marigram/update-marigram/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

const renderTsunamiLink = id => (
  <Link aria-label={`Tsunami Event ${id}`} to={`/view/hazards/tsunami/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <ExternalLinkIcon ariaLabel="Link To Tsunami Event" tooltip="View Related Tsunami Event" />
  </Link>
);

// const buildURL = url => (
//   (url !== undefined) && (
//     <a aria-label={`Data Download ${/[^/]*$/.exec(url)[0]}`} href={url} target="_blank" rel="noopener noreferrer">
//       <DownloadDataIcon aria-label="Data Download" tooltip="View File" />
//     </a>
//   )
// );

const buildURL = (url, size) => (
  (url !== undefined) && (
    <span className="externalLink">
      <a aria-label={`DOI Reference ${/[^/]*$/.exec(url)[0]}`} href={url} target="_blank" rel="noopener noreferrer">
        <DownloadDataIcon aria-label="Data Download" tooltip="View File" />
      </a>
      <span className="externalLinkText">
        (
        {(size < 1000000) ? '<1' : size / 1000000}
        )
      </span>
    </span>
  )
);

const buildDOI = url => (
  (url !== undefined) && (
    <span>
      <span className="externalLinkDOI">{url.substr(19)}</span>
      <a aria-label={`DOI Reference ${/[^/]*$/.exec(url)[0]}`} href={url} target="_blank" rel="noopener noreferrer">
        <span className="externalLinkIcon"><ExternalLinkIcon ariaLabel="Go to Metadata Landing Page" tooltip="Go to Metadata Landing Page" /></span>
      </a>
    </span>
  )
);

export class MarigramDataGrid extends Component {
  constructor(props) {
    super(props);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.focus = this.focus.bind(this);
    this.handleYesClick = this.handleYesClick.bind(this);
    this.deleteCancel = null;
    this.state = {
      modal: false,
      selectedId: null,
    };
  }

  componentDidUpdate(prevProps) {
    const { deleting, onDeleteCloseModal } = this.props;
    if (!deleting && prevProps.deleting) {
      this.closeModal(() => {
        onDeleteCloseModal();
      });
    }
  }

  openModal(selectedId) {
    this.setState({
      modal: true,
      selectedId,
    });
  }

  closeModal(callback) {
    this.setState({
      modal: false,
      selectedId: null,
    }, callback);
  }

  handleDeleteClick(id) {
    this.openModal(id);
  }

  focus() {
    this.deleteCancel.focus();
  }

  handleYesClick() {
    const { deleteMarigram } = this.props;
    const { selectedId } = this.state;
    deleteMarigram({ id: selectedId });
  }

  render() {
    const {
      marigrams,
      dataManager,
      sort,
      visibleColumns,
      toggleColumns,
      searchParams,
      deleting,
    } = this.props;

    const { modal } = this.state;

    return (
      <div className="haz-data-grid-container">

        <Modal isOpen={modal || deleting} onOpened={this.focus} backdrop="static">
          <ModalHeader>Delete Marigram</ModalHeader>
          <ModalBody>
            {deleting ? 'Deleting...' : 'Are you sure you want to delete the marigram?'}
          </ModalBody>
          <ModalFooter>
            <Button disabled={deleting} color="danger" onClick={this.handleYesClick}>Delete</Button>
            {' '}
            <Button disabled={deleting} color="secondary" innerRef={(ref) => { this.deleteCancel = ref; }} onClick={() => this.closeModal()}>Cancel</Button>
          </ModalFooter>
        </Modal>

        <ResultGrid
          data={marigrams}
          sort={sort}
          columnChooser
          visibleColumns={visibleColumns}
          handleColumnsToggle={toggleColumns}
          download
          downloadPrefix="marigrams"
          downloadSearchParams={searchParams}
          defaultColumns={defaultColumns}
          defaultSort={defaultSort}
          minColumns={minColumns}
        >
          {dataManager && (
            <Column alwaysVisible id="del" cell={({ row }) => (<DeleteIcon handleClick={() => this.handleDeleteClick(row.get('id'))} />)} width={30}>
              <DeleteIcon />
            </Column>
          )}
          {dataManager && (
            <Column alwaysVisible id="edit" cell={({ row }) => renderEditLink(row.get('id'))} width={30}>
              <EditIcon />
            </Column>
          )}
          {dataManager && (
            <Column alwaysVisible id="id" width={50} sortable>Id</Column>
          )}
          {dataManager && (
            <Column alwaysVisible id="publish" cell={({ value }) => (value ? 'Y' : 'N')} width={45} sortable>Publish</Column>
          )}
          <Column id="year" width={45} sortable helpModalId="marYear">Year</Column>
          <Column id="month" width={45} sortable helpModalId="month">Mo</Column>
          <Column id="day" width={45} sortable helpModalId="day">Dy</Column>
          <Column id="regionCode" width={45} sortable helpModalId="tsuRegion">Region</Column>
          <Column id="country" width={200} sortable helpModalId="marCountry">Country</Column>
          <Column id="state" width={150} sortable helpModalId="marState">Area</Column>
          <Column id="locationName" width={250} sortable helpModalId="marLocName">Location Name</Column>
          <Column id="latitude" width={100} sortable helpModalId="marLatitude">Latitude</Column>
          <Column id="longitude" width={100} sortable helpModalId="marLongitude">Longitude</Column>
          <Column id="tsunamiEventId" width={50} helpModalId="tsuMoreInfo" cell={({ value }) => value && renderTsunamiLink(value)}>Event</Column>
          <Column id="scanPreview" width={85} cell={({ row }) => buildURL(row.get('scanPreview'), row.get('scanPreviewSize'))}>Scan Preview (MB)</Column>
          <Column id="plotPreview" width={85} cell={({ row }) => buildURL(row.get('plotPreview'), row.get('plotPreviewSize'))}>Plot Preview (MB)</Column>
          <Column id="scan" width={85} cell={({ row }) => buildURL(row.get('scan'), row.get('scanSize'))}>Scan Full Record (MB)</Column>
          <Column id="netcdf" width={85} cell={({ row }) => buildURL(row.get('netcdf'), row.get('netcdfSize'))}>NetCDF (MB)</Column>
          <Column id="csv" width={85} cell={({ row }) => buildURL(row.get('csv'), row.get('csvSize'))}>CSV (MB)</Column>
          <Column id="reference" width={150} cell={({ row }) => buildDOI(row.get('reference'))}>DOI</Column>
          {/* have it say view doi */}
        </ResultGrid>
      </div>
    );
  }
}

MarigramDataGrid.propTypes = {
  marigrams: ImmutablePropTypes.list.isRequired,
  dataManager: PropTypes.bool.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  searchParams: PropTypes.string.isRequired,
  deleting: PropTypes.bool.isRequired,
  deleteMarigram: PropTypes.func.isRequired,
  onDeleteCloseModal: PropTypes.func.isRequired,
};
